import { format, parse, parseISO } from 'date-fns';
import { JobStatusEnum } from '~/models';
import { IOffsetPaginationMapper } from '~/types/Pagination';
import { tryCatch } from '~/utils/TryCatch';
import { OnsiteInterviewStatus, OnsiteInterviewStatusMapper } from '../enums';
import { ICreateOnsiteInterviewPayload, IGetOnsiteInterviewListPayload } from '../types';
import {
  IGetOnsiteInterviewListResult,
  IGetOnsiteInterviewListViewEntity,
  TGetOnsiteInterviewStatusCountResult,
} from '../types/OnsiteInterview';
import {
  ICreateOnsiteInterviewApiRequestBody,
  IGetOnsiteInterviewListApiResponse,
  IGetOnsiteInterviewStatusCountApiResponse,
} from '../types/OnsiteInterview.api';
import { UserSubProfession } from '~/enums/UserProfession';

/**
 * Helper function to parse break time string to minutes
 * Handles formats: "30 min", "1 hr", "1.5 hr", "2 hr", etc.
 */
const parseBreakTime = (breakTime: string | null): number | null => {
  if (!breakTime) return null;

  // Extract the number from the string (handles decimals)
  const match = breakTime.match(/(\d+\.?\d*)/);
  if (!match) return null;

  const value = parseFloat(match[1]);
  if (isNaN(value)) return null;

  // Check if it's in hours format
  if (breakTime.toLowerCase().includes('hr')) {
    // Convert hours to minutes
    return Math.round(value * 60);
  }

  // Otherwise it's in minutes
  return Math.round(value);
};

/**
 * Helper function to format ISO date string to YYYY-MM-DD format
 * Returns original value if parsing fails
 */
const formatLocalDate = (isoDateString: string): string => {
  const result = tryCatch(() => format(parseISO(isoDateString), 'yyyy-MM-dd'));
  return result.ok ? result.data : isoDateString;
};

/**
 * Helper function to convert 12-hour time format to 24-hour format
 * Handles format: "hh:mm a" (e.g., "09:00 am", "05:00 pm")
 * Returns original value if parsing fails
 */
const formatLocalTime = (timeString: string): string => {
  const result = tryCatch(() => {
    const parsedTime = parse(timeString, 'hh:mm a', new Date());
    return format(parsedTime, 'HH:mm');
  });
  return result.ok ? result.data : timeString;
};

const toCreateOnsiteInterviewApiRequestBody = (
  payload: ICreateOnsiteInterviewPayload,
): ICreateOnsiteInterviewApiRequestBody => {
  return {
    onsiteInterviews: payload.onsiteInterviews.map((interview) => {
      const isEFDA = interview.profession === UserSubProfession.EFDA;

      return {
        breakTime: parseBreakTime(interview.breakTime),
        localDate: formatLocalDate(interview.date),
        rate: parseFloat(interview.hourlyRate),
        localStart: formatLocalTime(interview.hoursArrival),
        localEnd: formatLocalTime(interview.hoursDeparture),
        isAssistedHygiene: interview.isAssistedHygiene,
        isBreakPaid: interview.isPaidBreakTime,
        profession: isEFDA
          ? (UserSubProfession.getProfession(interview.profession) ?? interview.profession ?? '')
          : (interview.profession ?? ''),
        specialty: interview.specialty,
        subProfession: isEFDA ? interview.profession : undefined,
      };
    }),
    duplicate: payload.duplicate,
  };
};

const toGetOnsiteInterviewListApiQueryParams = (
  payload: IGetOnsiteInterviewListPayload,
): Record<string, any> => {
  let status: JobStatusEnum[] = [];

  if (payload.status === 'all') {
    status = [
      ...OnsiteInterviewStatusMapper.toJobStatus(OnsiteInterviewStatus.ActionNeeded),
      ...OnsiteInterviewStatusMapper.toJobStatus(OnsiteInterviewStatus.Open),
      ...OnsiteInterviewStatusMapper.toJobStatus(OnsiteInterviewStatus.Scheduled),
    ];
  } else {
    status = OnsiteInterviewStatusMapper.toJobStatus(payload.status);
  }

  const params = {
    orderBy: payload.orderBy,
    orderDirection: payload.orderDirection,
    page: payload.page,
    pageSize: payload.limit,
    status: status.join(','),
  };

  return Object.fromEntries(Object.entries(params).filter(([, value]) => !!value));
};

const toGetOnsiteInterviewListResult = (
  response: IGetOnsiteInterviewListApiResponse,
): IGetOnsiteInterviewListResult => {
  return {
    data: response.data.map(
      (it) =>
        ({
          id: it.id,
          localDate: it.localDate,
          localTimeEndsAt: it.localEndTime,
          localTimeStartsAt: it.localStartTime,
          professional: it.professional
            ? {
                fullName: it.professional.fullName,
                id: it.professional.id,
                imageUrl: it.professional.profileImage,
              }
            : null,
          rate: it.rate,
          status: it.jobStatus as JobStatusEnum,
          totalCandidatesCount: it.candidatesCount,
        }) satisfies IGetOnsiteInterviewListViewEntity,
    ),
    pagination: IOffsetPaginationMapper.fromApi(response.metadata.pagination),
  } satisfies IGetOnsiteInterviewListResult;
};

const toGetOnsiteInterviewStatusCountResult = (
  response: IGetOnsiteInterviewStatusCountApiResponse,
): TGetOnsiteInterviewStatusCountResult => {
  return {
    all: response.data.active,
    [OnsiteInterviewStatus.ActionNeeded]: response.data.actionNeeded,
    [OnsiteInterviewStatus.Cancelled]: response.data.cancelledExpired,
    [OnsiteInterviewStatus.Completed]: response.data.completed,
    [OnsiteInterviewStatus.Open]: response.data.open,
    [OnsiteInterviewStatus.Scheduled]: response.data.scheduled,
  } satisfies TGetOnsiteInterviewStatusCountResult;
};

export const OnsiteInterviewAdapters = {
  toCreateOnsiteInterviewApiRequestBody,
  toGetOnsiteInterviewListApiQueryParams,
  toGetOnsiteInterviewListResult,
  toGetOnsiteInterviewStatusCountResult,
};
